import React from 'react';
import { Box, Typography } from '@heycater/design-system';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import CheckedIcon from '@icons/checked.svg';
import ClockIcon from '@icons/clock.svg';
import DeliveryIcon from '@icons/delivery.svg';
import ShoppingCartIcon from '@icons/shopping-cart.svg';
import { Caterer } from 'lib/algolia/model';
import Rating from 'qualification/components/CatererCard/Ratings';
import { useBreakpointUp } from 'qualification/hooks/useBreakpoints';
import useLocalizedFieldName from 'shared/hooks/useLocalizedFieldName';
import QualityAndSafetyIcon from 'shared/svg/QualityAndSafetyIcon';

const CARD_WIDTH = 335;
const CARD_WIDTH_MD = 260;

interface Props {
  caterer: Caterer;
}

export default function CatererCard({ caterer }: Props) {
  const { t } = useTranslation('static_pages');
  const { localizedFieldAlgolia } = useLocalizedFieldName();

  const isCertified =
    caterer.quality_safety_rating === 'Quality Proven Partner';

  const isSmUp = useBreakpointUp('sm');

  return (
    <StyledCatererCard>
      <Box position="relative">
        <ImageWrapper>
          {caterer.profile_image && (
            <Image
              alt=""
              height={isSmUp ? '126px' : '65px'}
              width={CARD_WIDTH}
              layout="responsive"
              src={caterer.profile_image}
              objectFit="cover"
            />
          )}
        </ImageWrapper>
        <StyledPill>
          <Typography
            lineHeight={1}
            color={'rgba(0,0,0, 0.6)'}
            fontWeight={600}
            variant="caption"
          >
            {t('catererCards.yourSelectedCaterer')}
          </Typography>
        </StyledPill>
      </Box>

      <CatererInformationWrapper>
        <CatererInformation>
          <CardTitleTypography>
            {caterer.company_name}{' '}
            {isCertified ? <QualityAndSafetyIcon fontSize="small" /> : null}
          </CardTitleTypography>
        </CatererInformation>
        <SloganTypography variant="body2">
          {caterer[localizedFieldAlgolia('slogan')]}
        </SloganTypography>
        <div id="details">
          <Divider />
          {caterer.rating_score && (
            <Box display="flex" alignItems="center" my={1.5} mb={2}>
              <Rating
                score={caterer.rating_score}
                ratingCount={caterer.number_of_ratings}
              />
            </Box>
          )}

          <InfoGrid>
            <InfoIcon>
              <CheckedIcon height="17" width="17" fontSize={'inherit'} />
            </InfoIcon>

            <InfoTypography variant="caption" ml={1}>
              {caterer.number_of_orders} Caterings
            </InfoTypography>

            <InfoIcon>
              <DeliveryIcon />
            </InfoIcon>

            <InfoTypography variant="caption" fontSize={12} ml={1} mr={1.5}>
              {caterer.minimum_delivery_fee === 0
                ? t('catererCards.freeDelivery', {
                    amount: wholeNumberWithCurrencySymbol(
                      caterer.minimum_delivery_fee
                    ),
                  })
                : t('catererCards.delivery', {
                    amount: wholeNumberWithCurrencySymbol(
                      caterer.minimum_delivery_fee
                    ),
                  })}
            </InfoTypography>

            <InfoIcon>
              <ShoppingCartIcon />
            </InfoIcon>

            <InfoTypography variant="caption" ml={1}>
              {t('catererCards.orderFrom', {
                amount: caterer.minimum_order_value / 100,
              })}
            </InfoTypography>
            <InfoIcon>
              <ClockIcon />
            </InfoIcon>
            <InfoTypography variant="caption" ml={1}>
              {t('catererCards.leadTime', {
                days: caterer.numeric_lead_time,
                count: caterer.numeric_lead_time,
              })}
            </InfoTypography>
          </InfoGrid>
        </div>
      </CatererInformationWrapper>
    </StyledCatererCard>
  );
}

function wholeNumberWithCurrencySymbol(number: number) {
  return `${Math.floor(number / 100)}€`;
}

const InfoTypography = styled(Typography)`
  color: ${({ theme }) => theme.palette.grey.secondary};
  font-size: 14px;

  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: 16px;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  color: #3c4340;
  grid-template-columns: 17px 1fr;
  align-items: center;
  row-gap: ${({ theme }) => theme.spacing(1.5)}px;
`;

const StyledCatererCard = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  border: 1px solid #007f33;
  background-color: #f7fcfa;

  border-radius: ${(props) => props.theme.spacing(1)}px;

  img {
    width: 100%;
    display: block;
    position: relative;
    transition: transform 0.5s ease, filter 0.5s ease;
  }
  overflow: hidden;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    width: ${CARD_WIDTH_MD}px;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    width: ${CARD_WIDTH}px;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
`;

const CatererInformation = styled.div`
  display: flex;
  justify-content: space-between;
`;

const CatererInformationWrapper = styled.div`
  flex-grow: 1;
  padding: ${(props) => props.theme.spacing(2, 2)};
  min-height: 145px;
  ${(props) => props.theme.breakpoints.down('sm')} {
    min-height: 160px;
  }
  ${(props) => props.theme.breakpoints.down('xs')} {
    min-height: fit-content;
    #details {
      display: none;
    }
  }
`;

const TruncatedTypography = styled(Typography)`
  white-space: nowrap;
  position: relative;
  width: 100%;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    width: 40px;
    background: linear-gradient(to right, rgba(255, 255, 255, 0), #fff);
  }
`;

const CardTitleTypography = styled((props) => (
  <TruncatedTypography {...props} variant="h4" fontSize="18px" component="h4" />
))`
  color: #3c4340;
  font-weight: 600;
  font-size: 16px;
  line-height: 172.5%;

  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: 24px;
  }
`;

const SloganTypography = styled(TruncatedTypography)`
  font-size: 14px;

  line-height: 26.88px;
  color: rgba(0, 0, 0, 0.6);
  min-height: ${(props) => props.theme.spacing(1)}px;
  margin-top: ${(props) => props.theme.spacing(0.5)}px;

  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: 16px;
    font-weight: 500;
  }
`;

const StyledPill = styled(Box)`
  position: absolute;
  top: ${(props) => props.theme.spacing(2)}px;
  left: ${(props) => props.theme.spacing(2)}px;
  padding: ${(props) => props.theme.spacing(0.5, 1)};
  height: 28px;
  display: flex;

  color: #007f33;
  font-weight: 600;
  justify-content: center;
  align-items: center;
  background: #f5f5f5;
  border-radius: ${(props) => props.theme.spacing(0.5)}px;
`;

const Divider = styled.div`
  border: 1px solid ${(props) => props.theme.palette.grey[200]};
  height: 0px;
  margin: ${({ theme }) => theme.spacing(1, 0)};
  width: 100%;
`;

const InfoIcon = styled.div`
  justify-self: end;
  display: flex;
  align-items: center;
  height: auto;
`;
